import { TestScoresOptionDto } from 'src/app/core/dtos/dto';
import { RegistrationService } from './../../../core/services/registration.service';
import { setScoreReport } from './../../../state/reg-data.actions';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { selectRegData } from './../../../state/reg-data.selectors';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';

import { Step } from 'src/app/core/dtos/step';
import { RegWorkflowMappingService } from 'src/app/core/services/reg-workflow-mapping.service';
import { StepProgressService, PageStepEnum } from 'src/app/core/services/step-progress.service';
import { RegData, ScoreReportPageSaveInfo } from 'src/app/core/dtos/reg-data';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationRoute } from 'src/app/core/constants/constants';
import { RegWorkflowEnum, IdDocTypeEnum, BrandEnum } from 'src/app/core/dtos/enum';
import { HotToastService } from '@ngneat/hot-toast';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { SmsHelper } from 'src/app/core/services/helpers/smsHelper';
import { ScoreReportOptionsSelectorComponent } from 'src/app/account/pages/score-report-options/score-report-options-selector/score-report-options-selector.component';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { HttpErrorResponse } from '@angular/common/http';
import { TelInputDto } from 'src/app/core/dtos/tel-input-model';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-score-report',
  templateUrl: './score-report.component.html',
  styleUrls: ['./score-report.component.scss']
})
export class ScoreReportComponent implements OnInit, OnDestroy {
  @ViewChild(ScoreReportOptionsSelectorComponent) child: ScoreReportOptionsSelectorComponent;

  regDataInfo$: Observable<RegData>;
  setPhoneNumberSubscription: Subscription;
  getPhoneNumberSubscription: Subscription;
  regDataSubscription: Subscription;
  formGroup: FormGroup;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [CountryISO.Canada];
  SearchCountryField = SearchCountryField;
  phoneNumberFormat = PhoneNumberFormat.National;

  brandName: string;
  pagePath = NavigationRoute.REGISTRATION.scoreReport;
  regWorkFlow: RegWorkflowEnum;
  steps: Step[];
  continueClicked = true;
  expressRating = false;
  priorityShipping = false;
  phoneProvided: boolean;
  phoneNumber: string;
  testTypeId = 0;
  storeTestScoresOption: TestScoresOptionDto = null;
  regId: number;
  isWebDelivery: boolean;

  brandId: BrandEnum;
  showIrccOnNewInstitutions: boolean;
  validIrccDocs = [IdDocTypeEnum.Passport, IdDocTypeEnum.UnitedStatesPassportCard, IdDocTypeEnum.CanadianPermanentResidentCard, IdDocTypeEnum.CanadianRefugeeProtectionClaimantDocument, IdDocTypeEnum.RefugeeTravelDocument, IdDocTypeEnum.ComputerizedNationalIdentityCard];

  constructor(
    private regWorkflowMappingService: RegWorkflowMappingService,
    private stepProgressServiceService: StepProgressService,
    private router: Router,
    private toastService: HotToastService,
    private store: Store,
    private formBuilder: FormBuilder,
    private configHelper: ConfigHelper,
    private smsHelper: SmsHelper,
    private registrationService: RegistrationService
  ) {
    this.steps = this.stepProgressServiceService.getSteps(PageStepEnum.ScoreReport);
  }

  ngOnInit() {
    this.brandName = this.configHelper.getBrandName();
    this.brandId = this.configHelper.getBrandId();
    this.formGroup = this.formBuilder.group({
      phoneProvided: [false],
      phoneNumber: ['']
    });
    this.regDataInfo$ = this.store.pipe(select(selectRegData));
    this.regDataSubscription = this.regDataInfo$.pipe(take(1))
      .subscribe(regData => {
        this.regWorkFlow = this.regWorkflowMappingService.getRegWorkFlow(regData.testSession);
        this.testTypeId = regData.testSession.testTypeId;
        this.regId = regData.regId;
        this.isWebDelivery = regData.testSession.isWebDelivery;
        this.showIrccOnNewInstitutions = this.checkShowIrccOnNewInstitutions(regData.proofOfIdentity.documentTypeId);

        if (regData.scoreReport) {
          this.expressRating = regData.scoreReport.expressRating;
          this.priorityShipping = regData.scoreReport.priorityShipping;
          if (regData.scoreReport.phoneNumber) {
            this.formGroup.controls.phoneNumber.setValue(regData.scoreReport.phoneNumber);
          }
          if (regData.scoreReport.testScoresOptionDto) {
            this.storeTestScoresOption = regData.scoreReport.testScoresOptionDto;
          }
          this.formGroup.controls.phoneProvided.setValue(regData.scoreReport.smsNotification);
          this.continueClicked = true;
        } else {
          this.storeTestScoresOption = this.createTestScoreOptions();
        }
      });
    this.getPhoneNumber();
  }

  checkShowIrccOnNewInstitutions(documentTypeId: number) {
    if (this.validIrccDocs.includes(Number(documentTypeId))) {
      return true;
    }
    else {
      return false;
    }
  }

  getPhoneNumber() {
    this.getPhoneNumberSubscription = this.registrationService.getScoreReportPhoneNumber()
      .subscribe(result => {
        this.formGroup.patchValue({ phoneNumber: result?.content });
      });
  }

  ngOnDestroy() {
    this.regDataSubscription?.unsubscribe();
    this.setPhoneNumberSubscription?.unsubscribe();
    this.getPhoneNumberSubscription?.unsubscribe();
  }

  redirectNextStep() {
    const nextStep = this.regWorkflowMappingService.getNextStepNew(this.regWorkFlow, this.pagePath);
    this.router.navigate([nextStep]);
  }

  clickContinue() {
    this.continueClicked = true;
  }

  clickNextPage() {
    if (this.formGroup.controls.phoneProvided.value &&
      (!this.formGroup.controls.phoneNumber.value || this.formGroup.controls.phoneNumber.errors &&
       (this.formGroup.controls.phoneNumber.value.e164Number.length !== 12 || this.formGroup.controls.phoneNumber.value.e164Number.indexOf('753') !== 2))) {
      this.toastService.warning("Please enter a valid phone number.");
      return;
    }

    let convertedTelInput = this.formGroup.controls.phoneNumber.value as TelInputDto;
    if (!this.formGroup.controls.phoneProvided.value) {
      convertedTelInput = null;
    }
    this.setPhoneNumberSubscription = this.registrationService.setScoreReportPhoneNumber({
      allowSMSNotifications: this.formGroup.controls.phoneProvided.value,
      smsPhoneNumber: convertedTelInput ? this.smsHelper.formatPhoneNumber(convertedTelInput.e164Number) : "",
    })
      .subscribe(
        () => {
          this.setScoreReport();
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  setScoreReport() {
    const scoreReport: ScoreReportPageSaveInfo = {
      expressRating: this.expressRating,
      priorityShipping: this.priorityShipping,
      phoneNumber: this.formGroup.controls.phoneProvided.value ? this.formGroup.controls.phoneNumber.value.number : null,
      smsNotification: this.formGroup.controls.phoneProvided.value,
      testScoresOptionDto: this.child?.testScoresOption
    };

    if (this.child.Validate()) {
      if (this.child.testScoresOption.scoreRecipientRegs.length > 5) {
        this.toastService.warning("At most five institutions are allowed to add here. You may add more institutions at any time in your CAEL Account up to the day your scores are posted.");
        return;
      }
      this.store.dispatch(setScoreReport({ scoreReport }));
      this.registrationService.setTestScoresOptions(this.child.testScoresOption).subscribe(
        () => {
          this.redirectNextStep();
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
    }
  }

  selectExpressRating() {
    this.expressRating = true;
    this.priorityShipping = true;
  }

  selectStandardRating() {
    this.expressRating = false;
  }

  selectRegularShipping() {
    this.priorityShipping = false;
  }

  selectPriorityShipping() {
    this.priorityShipping = true;
  }

  createTestScoreOptions(): TestScoresOptionDto {
    return {
      regId: null,
      scoreRecipientRegs: [],
      scoreReportMailingOptionId: 0,
      shippingRequiredScoreLevelId: 0,
      shipScoreReport: false
    };
  }
}
